import { Title } from "../../components/base/Title";
import { useTabsNavigation } from "../../components/base/Tabs";

export default function CGU() {
  const { switchToTab } = useTabsNavigation();

  const handleLinkClick = (tabIndex: number) => {
    switchToTab(tabIndex);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto leading-relaxed">
      <Title type="subdisplay" centered>Conditions Générales d'Utilisation</Title>
      <p className="text-gray-600 mb-6 text-center">
        <strong>Dernière mise à jour :</strong> 18 octobre 2025
      </p>

      <div className="space-y-8">
        <section>
          <p className="mb-4">
            Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de la plateforme
            "Le Rucher de Carole et Sylvain" accessible à l'adresse web du site. En utilisant notre plateforme, vous acceptez
            pleinement et entièrement les présentes conditions.
          </p>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            1. Définitions et champ d'application
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Définitions</Title>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Plateforme :</strong> Le site web "Le Rucher de Carole et Sylvain" et ses fonctionnalités</li>
                <li><strong>Utilisateur :</strong> Toute personne accédant à la plateforme</li>
                <li><strong>Client :</strong> Utilisateur ayant créé un compte et effectuant des commandes</li>
                <li><strong>Produits :</strong> Articles apicoles (miel, produits dérivés) proposés à la vente</li>
                <li><strong>Livraison groupée :</strong> Système de livraison organisé par zones géographiques</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Champ d'application</Title>
              <p>
                Ces CGU s'appliquent à tous les utilisateurs de la plateforme, qu'ils soient simples visiteurs
                ou clients effectuant des commandes. L'utilisation de la plateforme implique l'acceptation
                pleine et entière des présentes conditions.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            2. Services proposés
          </Title>


          <div className="space-y-4">
            <p>
              La plateforme propose exclusivement des produits apicoles naturels : miel de printemps,
              miel d'été, et autres produits de la ruche, tous issus de la production locale de
              Carole et Sylvain LEBAS à Torcy-Le-Grand.
            </p>
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Fonctionnalités principales</Title>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Consultation du catalogue de produits apicoles</li>
                <li>Création et gestion de compte utilisateur</li>
                <li>Passation de commandes en ligne</li>
                <li>Paiement sécurisé via Stripe</li>
                <li>Suivi des commandes et livraisons</li>
                <li>Organisation de livraisons groupées</li>
                <li>Gestion des groupes de commande</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            3. Inscription et compte utilisateur
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Création de compte</Title>
              <p>
                L'inscription sur la plateforme est gratuite et obligatoire pour accéder aux fonctionnalités
                de la plateforme (consultation des produits, commandes, suivi des livraisons). 
                L'utilisateur s'engage à fournir des informations exactes et à maintenir ses données à jour.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Responsabilités de l'utilisateur</Title>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Protéger la confidentialité de ses identifiants de connexion</li>
                <li>Informer immédiatement en cas de compromission de son compte</li>
                <li>Utiliser la plateforme conformément à sa destination</li>
                <li>Respecter les droits des autres utilisateurs</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            4. Processus de commande et paiement
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Commande</Title>
              <p>
                Les commandes sont effectuées via le panier en ligne. L'utilisateur peut modifier sa commande
                avant validation. Avant le paiement, l'utilisateur doit obligatoirement cocher la case 
                "J'accepte les Conditions Générales de Vente" pour confirmer son acceptation des prix 
                et conditions de vente. 
                <a href="/cgv" className="text-primary-600 hover:underline ml-1">
                  (CGV)
                </a>
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Paiement</Title>
              <p>
                Le paiement s'effectue exclusivement en ligne via la plateforme Stripe, garantissant
                la sécurité des transactions. Les moyens de paiement acceptés sont : cartes bancaires,
                virements instantanés, et autres moyens proposés par Stripe.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Confirmation de commande</Title>
              <p>
                Toute commande payée vous sera confirmée par le Rucher de Carole et Sylvain par email.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            5. Livraisons et retours
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Système de livraison groupée</Title>
              <p>
                Les livraisons sont organisées par zones géographiques et par périodes définies.
                Les clients sont informés des dates de livraison lors de la commande.
                Le système de livraison groupée permet d'optimiser les coûts et l'impact environnemental.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Retours et remboursements</Title>
              <p>
                Les produits alimentaires ne peuvent être retournés pour des raisons d'hygiène.
                En cas de problème avec un produit (qualité, conformité), le client peut contacter
                le service client pour une solution adaptée.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            6. Prix et disponibilité
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Prix</Title>
              <p>
              Les prix sont indiqués en euros TTC. En tant qu'apiculteurs, nous ne sommes pas soumis à la TVA 
              (article 293B du CGI). Les prix applicables sont ceux en vigueur au moment de la commande.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Disponibilité des produits</Title>
              <p>
                La disponibilité des produits varie d'une récolte à une autre.
                En cas de rupture de stock, le client ne pourra pas commander ce produit jusqu'à prochaine récolte.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            7. Propriété intellectuelle
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Contenu de la plateforme</Title>
              <p>
                L'ensemble du contenu de la plateforme (textes, images, logos, design) est protégé
                par le droit d'auteur et appartient à Le Rucher de Carole et Sylvain ou à ses partenaires.
                Toute reproduction est interdite sans autorisation préalable.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Utilisation autorisée</Title>
              <p>
                L'utilisateur peut consulter et utiliser la plateforme pour ses besoins personnels
                uniquement. Toute utilisation commerciale ou de reproduction est interdite.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            8. Protection des données personnelles
          </Title>

            <p className="mb-4">
              Le traitement des données personnelles est régi par notre
              <button 
                onClick={() => handleLinkClick(2)} 
                className="text-primary-600 hover:underline ml-1 cursor-pointer"
              >
                politique de confidentialité
              </button>.
              En utilisant la plateforme, vous acceptez le traitement de vos données dans les conditions
              décrites dans cette politique.
            </p>

          <div className="border border-gray-200 rounded-lg p-4">
            <Title type="h3" className="font-semibold mb-2">Données collectées</Title>
            <p>
              Nous collectons les données nécessaires à la gestion de votre compte, au traitement
              de vos commandes et à l'organisation des livraisons : nom, prénom,
              coordonnées de contact, informations de paiement (via Stripe).
            </p>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            9. Responsabilités et limitations
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Disponibilité du service</Title>
              <p>
                Nous nous efforçons de maintenir la plateforme accessible 24h/24, mais ne pouvons
                garantir une disponibilité absolue. Des interruptions peuvent survenir pour
                maintenance ou cas de force majeure.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Limitation de responsabilité</Title>
              <p>
                Dans les limites autorisées par la loi, Le Rucher de Carole et Sylvain ne pourra être tenu
                responsable des dommages indirects résultant de l'utilisation de la plateforme
                ou de l'impossibilité de l'utiliser.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Force majeure</Title>
              <p>
                Le Rucher de Carole et Sylvain ne pourra être tenu responsable de l'inexécution de ses obligations
                en cas de force majeure, notamment en cas de problème de production apicole,
                de conditions climatiques défavorables, ou d'événements extérieurs.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            10. Utilisation conforme
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Utilisation autorisée</Title>
              <p>
                La plateforme doit être utilisée conformément à sa destination : consultation
                et commande de produits apicoles. Toute utilisation détournée est interdite.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Interdictions</Title>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Utilisation de la plateforme à des fins illégales</li>
                <li>Tentative de piratage ou d'intrusion</li>
                <li>Transmission de contenus malveillants</li>
                <li>Usurpation d'identité</li>
                <li>Spam ou envoi de messages non sollicités</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            11. Droit applicable et juridiction
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Droit applicable</Title>
              <p>
                Les présentes CGU sont soumises au droit français. En cas de conflit entre
                les versions en différentes langues, la version française prévaut.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Juridiction compétente</Title>
              <p>
                En cas de litige, les tribunaux français seront seuls compétents.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            12. Contact et réclamations
          </Title>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Service client</Title>
              <p>
                Pour toute question, réclamation ou demande d'information concernant ces CGU
                ou l'utilisation de la plateforme :
              </p>
              <p className="mt-2">
                <strong>E-mail :</strong>
                <a href="mailto:contact.leruchernormand@gmail.com" className="text-primary-600 hover:underline ml-1">
                  contact.leruchernormand@gmail.com
                </a>
              </p>
              <p>
                <strong>Adresse :</strong> 67 rue du stade, 76590 Torcy-Le-Grand, France
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <Title type="h3" className="font-semibold mb-2">Médiation</Title>
              <p>
                En cas de litige non résolu à l'amiable, le client peut recourir à la médiation
                de la consommation conformément aux dispositions du Code de la consommation.
              </p>
            </div>
          </div>
        </section>

        <section>
          <Title type="h2" className="font-semibold mb-4 text-primary-600">
            13. Modifications des CGU
          </Title>

          <div className="border border-gray-200 rounded-lg p-4">
            <p>
              Le Rucher de Carole et Sylvain se réserve le droit de modifier les présentes CGU à tout moment.
              Les modifications entrent en vigueur dès leur publication sur la plateforme.
              L'utilisation continue de la plateforme après modification vaut acceptation
              des nouvelles conditions.
            </p>
            <p className="mt-4">
              Nous vous encourageons à consulter régulièrement cette page pour prendre connaissance
              des éventuelles modifications.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
